import { useMemo } from 'react';
import {
  Code,
  DataTable,
  FormattedRelativeTime,
  Stack,
  Text,
  TextLabel,
  useSnackbars,
} from '@customerio/pluma-components/react';
import type { EnvironmentsEventAttributeSummary } from '@customerio/hydra-ui-api';
import { useUpdateDataIndexEventAttributePrivacy } from '@/services/db/data-index/mutations';
import { useIsRedacted } from '@/services/permissions/hooks';
import { NOT_SENSITIVE_PRIVACY_LEVEL, SENSITIVE_PRIVACY_LEVEL } from '../utils/types';

/**
 * The attributes an event carries, and when each was last sent.
 *
 * A row expands to show the values that have actually arrived, because the
 * name of an attribute rarely says what is in it. Marking one sensitive is
 * the only thing that can be changed here: the names and values come from
 * ingestion.
 */

/** The privacy level the backend uses for an attribute someone marked sensitive. */

type EventAttributesTableProps = {
  workspaceId: number;
  eventName: string;
  attributes: readonly EnvironmentsEventAttributeSummary[];
  /** Whether the plan includes redacting attributes from teammates. */
  canMarkSensitive: boolean;
};

export function EventAttributesTable({
  workspaceId,
  eventName,
  attributes,
  canMarkSensitive,
}: EventAttributesTableProps) {
  const snackbars = useSnackbars();
  const setPrivacy = useUpdateDataIndexEventAttributePrivacy({ workspaceId });

  const columns = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: 'Attribute name',
        cell: ({ row }: { row: { original: EnvironmentsEventAttributeSummary } }) => <Text>{row.original.name}</Text>,
      },
      {
        accessorKey: 'last_seen_at',
        header: 'Last seen',
        cell: ({ row }: { row: { original: EnvironmentsEventAttributeSummary } }) => {
          const at = row.original.last_seen_at;
          return at == null || at === 0 ? <Text>—</Text> : <FormattedRelativeTime date={at * 1000} withTooltip />;
        },
      },
    ],
    [],
  );

  const changePrivacy = (attribute: EnvironmentsEventAttributeSummary) => {
    const isSensitive = attribute.privacy_level === SENSITIVE_PRIVACY_LEVEL;
    const privacyLevel = isSensitive ? NOT_SENSITIVE_PRIVACY_LEVEL : SENSITIVE_PRIVACY_LEVEL;

    setPrivacy.mutate(
      { eventName, attributeName: attribute.name, privacyLevel },
      {
        onSuccess: () => {
          snackbars.create({
            id: 'event-attribute.change-sensitivity',
            message: `${attribute.name} has been marked as ${isSensitive ? 'not sensitive' : 'sensitive'}.`,
          });
        },
        onError: () => {
          snackbars.create({
            id: 'event-attribute.change-sensitivity.error',
            variant: 'critical',
            message: "An error occurred when changing this attribute's sensitivity.",
          });
        },
      },
    );
  };

  const rowActions = (row: { original: EnvironmentsEventAttributeSummary }) => {
    if (!canMarkSensitive) {
      return {};
    }
    const isSensitive = row.original.privacy_level === SENSITIVE_PRIVACY_LEVEL;

    return {
      dropdownActions: [
        {
          label: isSensitive ? 'Make insensitive' : 'Make sensitive',
          icon: isSensitive ? ('view-on' as const) : ('view-off' as const),
          onClick: () => changePrivacy(row.original),
        },
      ],
    };
  };

  return (
    <DataTable
      caption="Event attributes"
      data={[...attributes]}
      columns={columns}
      withSorting
      withSearch
      withPagination
      withRowExpanding
      getRowCanExpand={() => true}
      expandedRowComponent={SampleValues}
      rowActions={rowActions}
    />
  );
}

type SampleValuesProps = {
  row: { original: EnvironmentsEventAttributeSummary };
};

/** The values that have actually arrived for one attribute. */
function SampleValues({ row }: SampleValuesProps) {
  const isRedacted = useIsRedacted();
  const values = row.original.recent_values ?? [];

  // Ingested values are a customer's own data, and the page this replaces
  // shows them to anyone who can open it. Every other surface in this app
  // that renders customer values hides them from a viewer without
  // `sensitive_data/view`, and that rule wins over parity here.
  if (isRedacted) {
    return (
      <Text color="placeholder" size="xs">
        You do not have permission to view sample values.
      </Text>
    );
  }

  if (values.length === 0) {
    return (
      <Text color="placeholder" size="xs">
        Sample values will appear here as new events are sent.
      </Text>
    );
  }

  return (
    <Stack gap="100">
      <TextLabel color="subtle" variant="secondary" size="xxs">
        Sample value(s)
      </TextLabel>
      <Stack gap="050">
        {values.map((value) => (
          // Ingested values are the workspace's own customer data.
          <Code key={value} size="xxs" className="fs-mask">
            {value}
          </Code>
        ))}
      </Stack>
    </Stack>
  );
}
